// PRD.326 — the print surface for a patient form.
//
// SAME PATTERN AS PRESCRIPTION/INVOICE PRINTING: a hidden iframe, printed via
// `iframe.contentWindow.print()`. The host page is never touched — no
// `visibility: hidden` over the whole app, no offscreen node dragged back to
// the origin by print CSS, and nothing to undo if the user cancels.
//
// ONE DIFFERENCE FROM PRESCRIPTION. That path serializes with
// `renderToStaticMarkup` and writes a string into the iframe. That cannot work
// here: PRD.324's pagination measures real line boxes and page geometry, and a
// static string never runs React, so no measurement would happen and the form
// would print as one unpaginated page.
//
// So the form is PORTALED into the iframe's body instead. React context flows
// through the React tree rather than the DOM tree, so PowerSync, auth and brand
// tokens all still work, while layout and printing belong entirely to the
// iframe.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { FormPrintTarget } from './form-print';
import { waitForPrintReady } from './form-print';
import { FormRendererPage } from './FormRendererPage';

/** A4 at 96dpi — a real layout width for the print document. */
const A4_PRINT_WIDTH_PX = 794;
const A4_PRINT_HEIGHT_PX = 1123;

export function FormPrintMount({
  target,
  onDone,
}: {
  /** The form to print, or null when idle — no iframe exists while idle. */
  target: FormPrintTarget | null;
  /** Called once the dialog closes, whether printed or cancelled. */
  onDone: () => void;
}) {
  const [body, setBody] = useState<HTMLElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const printedRef = useRef(false);

  // ── Create / tear down the hidden iframe ──
  useEffect(() => {
    if (!target || typeof document === 'undefined') return;
    printedRef.current = false;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.title = 'Print form';
    // MUST HAVE A REAL SIZE. Prescription prints fixed-width (210mm) markup, so
    // its iframe can be 0x0. This document is width-driven: sheet height comes
    // from the A4 aspect ratio applied to the rendered width, and pagination
    // measures against that box. At width 0 every sheet collapses to nothing —
    // which is exactly the blank printout.
    //
    // So it is given A4-at-96dpi and parked offscreen instead of being
    // collapsed.
    Object.assign(iframe.style, {
      position: 'fixed',
      left: '-10000px',
      top: '0',
      width: `${A4_PRINT_WIDTH_PX}px`,
      height: `${A4_PRINT_HEIGHT_PX}px`,
      border: '0',
      opacity: '0',
      pointerEvents: 'none',
    });
    document.body.appendChild(iframe);
    iframeRef.current = iframe;

    const doc = iframe.contentDocument;
    if (!doc) {
      onDone();
      return;
    }

    doc.open();
    doc.write('<!doctype html><html><head><meta charset="utf-8" /></head><body></body></html>');
    doc.close();

    // The iframe starts with no CSS at all, so the app's stylesheets — and with
    // them every `var(--…)` token the form renders with — are copied across.
    for (const node of Array.from(
      document.head.querySelectorAll('style, link[rel="stylesheet"]'),
    )) {
      doc.head.appendChild(node.cloneNode(true));
    }

    const pageStyle = doc.createElement('style');
    pageStyle.textContent = PRINT_PAGE_STYLES;
    doc.head.appendChild(pageStyle);

    setBody(doc.body);

    return () => {
      setBody(null);
      iframeRef.current = null;
      // Deferred: on iPad the print sheet re-renders from this iframe when the
      // user changes settings, so it must outlive the first `afterprint`.
      setTimeout(() => {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }, 3000);
    };
  }, [target, onDone]);

  // ── Print once the document inside the iframe is ready ──
  useEffect(() => {
    if (!target || !body || printedRef.current) return;
    let cancelled = false;

    void (async () => {
      await waitForPrintReady(() => body);
      if (cancelled || printedRef.current) return;

      // Never open an empty dialog over a form that failed to render.
      if (!body.querySelector('.render-page')) {
        onDone();
        return;
      }

      const w = iframeRef.current?.contentWindow;
      if (!w) {
        onDone();
        return;
      }

      printedRef.current = true;
      const finish = () => {
        w.removeEventListener('afterprint', finish);
        onDone();
      };
      w.addEventListener('afterprint', finish);
      try {
        w.focus();
        w.print();
      } catch {
        finish();
        return;
      }
      // Watchdog for browsers that never fire `afterprint`.
      setTimeout(finish, 60000);
    })();

    return () => {
      cancelled = true;
    };
  }, [target, body, onDone]);

  if (!target || !body) return null;

  // Portaled, so the form keeps every React context while living in the
  // iframe's document.
  return createPortal(<FormRendererPage overrideIds={target} embedded printMode />, body);
}

/**
 * Page setup inside the print iframe.
 *
 * Only page geometry and sheet breaks are needed — the iframe contains nothing
 * but the form, so there is no app chrome to hide.
 */
const PRINT_PAGE_STYLES = `
  @page { size: A4 portrait; margin: 0; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    /* The document is taller than the iframe; nothing may clip it or the
       printer only receives the first sheet. */
    height: auto;
    overflow: visible;
  }

  .render-document {
    padding: 0 !important;
    gap: 0 !important;
    display: block !important;
    align-items: stretch !important;
  }

  .render-page {
    /* One rendered sheet → exactly one physical sheet. */
    break-after: page;
    page-break-after: always;
    break-inside: avoid;
    page-break-inside: avoid;
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
  }

  /* No break after the LAST sheet — that is what produces a trailing blank page. */
  .render-page:last-child { break-after: auto; page-break-after: auto; }

  /* Ask for template artwork and colour bands. The user's "Background graphics"
     setting still wins; nothing here can override it. */
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }

  [data-print-hide] { display: none !important; }
`;
